import { cognitoConfig } from '@/lib/cognito-config'

// Explicit Cognito Hosted UI logout (ADR-0011). Cognito's OIDC
// discovery omits `end_session_endpoint`, so we build the `/logout` URL directly
// from validated config — NEVER `signoutRedirect()`. The logout destination comes
// ONLY from configuration (never query/router/storage/props/backend/user input).

export interface LogoutConfig {
	domain?: string
	clientId?: string
	logoutUri?: string
}

/**
 * Build `${domain}/logout?client_id=…&logout_uri=…` from configuration only, with a
 * normalized domain (no trailing-slash double-slash). Returns `null` (fail closed)
 * when any required value is missing.
 */
export function buildCognitoLogoutUrl(config: LogoutConfig): string | null {
	if (!config.domain || !config.clientId || !config.logoutUri) return null
	const base = config.domain.replace(/\/+$/, '')
	const params = new URLSearchParams({ client_id: config.clientId, logout_uri: config.logoutUri })
	return `${base}/logout?${params.toString()}`
}

export interface PerformLogoutDeps {
	/** Clear Atlas current-user/query state + provider-owned OIDC user. */
	clearSession: () => Promise<void> | void
	/** Navigate the browser to the Hosted UI logout URL. */
	redirect: (url: string) => void
	/** Called when config is missing so the caller can show a safe error state. */
	onFailClosed?: () => void
	/** Defaults to the validated Cognito config. */
	config?: LogoutConfig
}

/**
 * Cleanup-first logout: clear local state BEFORE navigating, so cleanup still
 * happens if the hosted logout request fails. Fails closed (no redirect) when the
 * logout URL cannot be built from configuration.
 */
export async function performLogout(deps: PerformLogoutDeps): Promise<void> {
	await deps.clearSession()
	const config = deps.config ?? {
		domain: cognitoConfig?.domain,
		clientId: cognitoConfig?.clientId,
		logoutUri: cognitoConfig?.logoutUri,
	}
	const url = buildCognitoLogoutUrl(config)
	if (!url) {
		deps.onFailClosed?.()
		return
	}
	deps.redirect(url)
}
